import axios, { CanceledError } from "axios";
import { API_ORIGIN } from "@/lib/apiOrigin";
import { currentBearerToken, login, renew, signInAgain } from "@/lib/auth";
import { currentAppPath, isKeycloakMode } from "@/lib/authMode";
import { runtimeSession } from "@/runtime/session";
import { applyBearer, bindRuntimeSource, makeAuthErrorHandler } from "@/lib/authRetry";
import type {
  ConfigDrift,
  Transport,
  TransportCreate,
  TransportUpdate,
  Auth,
  AuthCreate,
  AuthUpdate,
  Aor,
  AorCreate,
  AorUpdate,
  Endpoint,
  EndpointCreate,
  EndpointUpdate,
  Identify,
  IdentifyCreate,
  IdentifyUpdate,
  DialplanContext,
  DialplanContextCreate,
  DialplanContextUpdate,
  DialplanExtension,
  DialplanExtensionCreate,
  DialplanExtensionUpdate,
  AriApp,
  AriAppCreate,
  AriAppUpdate,
  WebsocketEndpoint,
  WebsocketEndpointCreate,
  WebsocketEndpointUpdate,
  GenerateResult,
  ReloadResult,
  ActiveCall,
  CallGroup,
  OriginateCallRequest,
  AuditLog,
  ServerStatus,
  ConfigViewResponse,
  AmiCommandResponse,
  HepSettings,
  HepSettingsUpdate,
} from "@/types";

const api = axios.create({ baseURL: `${API_ORIGIN}/api`, withCredentials: false });

api.interceptors.request.use(async (config) => {
  const runtime = runtimeSession();
  bindRuntimeSource(config, runtime);
  if (runtime) {
    if (config.signal?.aborted || runtimeSession() !== runtime || runtime.signal.aborted) throw new CanceledError(undefined, config);
    return applyBearer(config, runtime.token());
  }
  const token = await currentBearerToken();
  if (config.signal?.aborted || runtimeSession() !== null) throw new CanceledError(undefined, config);
  return applyBearer(config, token);
});

api.interceptors.response.use(
  (response) => response,
  makeAuthErrorHandler({
    isKeycloak: isKeycloakMode,
    renew: () => renew(),
    token: currentBearerToken,
    retry: (config) => api.request(config),
    signIn: () => signInAgain(currentAppPath()),
    legacyLogin: () => login(),
    runtime: runtimeSession,
  }),
);

// Collection routes are registered with a trailing slash ("/api/transports/");
// requesting them without it triggers a 307 whose Location leaks the backend's
// internal host (asterisk.voicing.local:8000), unreachable from the browser.
function crud<T, C, U>(prefix: string) {
  return {
    list: (skip = 0, limit = 100) =>
      api.get<T[]>(`${prefix}/`, { params: { skip, limit } }).then((r) => r.data),
    get: (id: string) => api.get<T>(`${prefix}/${id}`).then((r) => r.data),
    create: (data: C) => api.post<T>(`${prefix}/`, data).then((r) => r.data),
    update: (id: string, data: U) =>
      api.put<T>(`${prefix}/${id}`, data).then((r) => r.data),
    delete: (id: string) => api.delete(`${prefix}/${id}`),
  };
}

export const transportsApi = crud<Transport, TransportCreate, TransportUpdate>(
  "/transports"
);
export const authsApi = crud<Auth, AuthCreate, AuthUpdate>("/auths");
export const aorsApi = crud<Aor, AorCreate, AorUpdate>("/aors");
export const endpointsApi = crud<Endpoint, EndpointCreate, EndpointUpdate>(
  "/endpoints"
);
export const identifiesApi = crud<Identify, IdentifyCreate, IdentifyUpdate>(
  "/identifies"
);
export const contextsApi = crud<
  DialplanContext,
  DialplanContextCreate,
  DialplanContextUpdate
>("/contexts");
export const ariAppsApi = crud<AriApp, AriAppCreate, AriAppUpdate>("/ari-apps");
export const wsEndpointsApi = crud<
  WebsocketEndpoint,
  WebsocketEndpointCreate,
  WebsocketEndpointUpdate
>("/websocket-endpoints");

export const extensionsApi = {
  list: (params?: { context_id?: string; skip?: number; limit?: number }) =>
    api
      .get<DialplanExtension[]>("/extensions/", { params })
      .then((r) => r.data),
  get: (id: string) =>
    api.get<DialplanExtension>(`/extensions/${id}`).then((r) => r.data),
  create: (data: DialplanExtensionCreate) =>
    api.post<DialplanExtension>("/extensions/", data).then((r) => r.data),
  update: (id: string, data: DialplanExtensionUpdate) =>
    api
      .put<DialplanExtension>(`/extensions/${id}`, data)
      .then((r) => r.data),
  delete: (id: string) => api.delete(`/extensions/${id}`),
};

export const configApi = {
  drift: () => api.get<ConfigDrift>("/config/drift").then((r) => r.data),
  generate: (module = "all") =>
    api
      .post<GenerateResult>("/config/generate", null, { params: { module } })
      .then((r) => r.data),
  reload: (module = "all") =>
    api
      .post<ReloadResult>("/config/reload", null, { params: { module } })
      .then((r) => r.data),
};

export interface CallHistoryParams {
  skip?: number;
  limit?: number;
  direction?: string;
  date_from?: string;
  date_to?: string;
}

export const callsApi = {
  active: () => api.get<ActiveCall[]>("/calls/active").then((r) => r.data),
  // /calls returns one CallGroup per call (per linked_id) with the raw per-channel
  // CallLog rows nested under `channels` — not one row per channel as it once did.
  history: (params: CallHistoryParams = {}) =>
    api.get<CallGroup[]>("/calls", { params }).then((r) => r.data),
  historyCount: (params: { direction?: string; date_from?: string; date_to?: string } = {}) =>
    api
      .get<{ count: number; channel_count: number }>("/calls/count", { params })
      .then((r) => r.data),
  get: (id: string) => api.get<CallGroup>(`/calls/${id}`).then((r) => r.data),
  hangup: (channel: string) =>
    api.post(`/calls/${encodeURIComponent(channel)}/hangup`).then((r) => r.data),
  transfer: (channel: string, destination: string) =>
    api
      .post(`/calls/${encodeURIComponent(channel)}/transfer`, { destination })
      .then((r) => r.data),
  originate: (data: OriginateCallRequest) =>
    api.post("/calls/originate", data).then((r) => r.data),
  extensions: () =>
    api.get<string[]>("/calls/extensions").then((r) => r.data),
};

export const auditLogsApi = {
  list: (params?: { skip?: number; limit?: number; table_name?: string; operation?: string }) =>
    api
      .get<AuditLog[]>("/audit-logs", { params })
      .then((r) => r.data),
  count: (params?: { table_name?: string; operation?: string }) =>
    api
      .get<{ count: number }>("/audit-logs/count", { params })
      .then((r) => r.data),
  tables: () => api.get<string[]>("/audit-logs/tables").then((r) => r.data),
};

export const statusApi = {
  get: () => api.get<ServerStatus>("/status").then((r) => r.data),
};

export const configViewApi = {
  get: (module: string) =>
    api.get<ConfigViewResponse>("/config/view", { params: { module } }).then((r) => r.data),
};

export const amiCommandApi = {
  run: (command: string) =>
    api.post<AmiCommandResponse>("/ami/command", { command }).then((r) => r.data),
};

export const logSettingsApi = {
  get: () =>
    api.get<{ debug_level: number; verbose_level: number }>("/logs/settings").then((r) => r.data),
};

export const hepSettingsApi = {
  get: () => api.get<HepSettings>("/hep-settings/").then((r) => r.data),
  update: (data: HepSettingsUpdate) =>
    api.put<HepSettings>("/hep-settings/", data).then((r) => r.data),
};

export interface Me {
  subject: string | null;
  email: string | null;
  username: string | null;
}

export const meApi = {
  get: () => api.get<Me>("/me").then((r) => r.data),
};
